import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, ChevronRight, Download, FileText, ListTree, Search, WifiOff, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { api, errMsg, downloadBlob } from "../../lib/api";

export const COMPLETE_KEY = "__complete__";

/* ---------- markdown renderer (tailwind-styled components) ---------- */
const mdComponents = {
  h1: ({ children }) => <h1 className="font-heading text-2xl font-semibold tracking-tight mt-2 mb-3">{children}</h1>,
  h2: ({ children }) => <h2 className="font-heading text-xl font-semibold tracking-tight mt-8 mb-3 pb-1.5 border-b">{children}</h2>,
  h3: ({ children }) => <h3 className="font-heading text-base font-semibold mt-6 mb-2">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold mt-4 mb-1.5">{children}</h4>,
  p: ({ children }) => <p className="text-sm leading-relaxed text-foreground/90 mb-3">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1 text-sm leading-relaxed">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1 text-sm leading-relaxed">{children}</ol>,
  li: ({ children }) => <li className="text-foreground/90">{children}</li>,
  a: ({ href, children }) => <a href={href} className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer">{children}</a>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  code: ({ inline, children, className }) => className || !inline && String(children).includes("\n")
    ? <code className="block font-mono text-xs bg-muted rounded-md p-3 overflow-x-auto">{children}</code>
    : <code className="font-mono text-[12.5px] bg-muted px-1.5 py-0.5 rounded">{children}</code>,
  pre: ({ children }) => <pre className="mb-3">{children}</pre>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-primary/40 bg-accent/40 rounded-r-md pl-4 pr-3 py-2 mb-3 text-sm [&>p]:mb-0">{children}</blockquote>,
  hr: () => <hr className="my-6 border-border" />,
  table: ({ children }) => <div className="overflow-x-auto mb-4 rounded-lg border"><table className="w-full text-sm">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-muted/60">{children}</thead>,
  th: ({ children }) => <th className="text-left font-semibold px-3 py-2 border-b">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 border-b last:border-0 align-top text-foreground/90">{children}</td>,
  tr: ({ children }) => <tr className="even:bg-muted/20">{children}</tr>,
};

export const Markdown = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{children || ""}</ReactMarkdown>
);

const chapterDomId = (g, c) => `ch-${g}-${c}`;

/* ---------- client-side helpers (mirror backend manual_engine) — used offline ---------- */
const escapeRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export function searchGuidesLocal(guides, q, limit = 50) {
  q = (q || "").trim();
  if (q.length < 2) return [];
  const rx = new RegExp(escapeRx(q), "gi");
  const out = [];
  for (const g of guides || []) {
    for (const c of g.chapters || []) {
      const text = c.content_md || "";
      const hits = [...text.matchAll(rx)];
      const titleHit = new RegExp(escapeRx(q), "i").test(c.title || "");
      if (!hits.length && !titleHit) continue;
      let snippet;
      if (hits.length) {
        const m = hits[0];
        const a = Math.max(0, m.index - 90), b = Math.min(text.length, m.index + m[0].length + 90);
        snippet = (a > 0 ? "…" : "") + text.slice(a, b).replace(/[#*`>|_-]+/g, " ").replace(/\n/g, " ").trim() + (b < text.length ? "…" : "");
      } else {
        snippet = text ? text.slice(0, 160).replace(/\n/g, " ") + "…" : "";
      }
      out.push({ guide_key: g.key, guide_title: g.title, chapter_key: c.key, chapter_title: c.title,
        matches: hits.length + (titleHit ? 1 : 0), snippet });
    }
  }
  return out.sort((a, b) => b.matches - a.matches).slice(0, limit);
}

export function guideToMarkdown(g) {
  const parts = [`# ${g.title}`, ""];
  for (const c of g.chapters || []) {
    if (!(c.key === "overview" && c.title === "Overview")) parts.push(`## ${c.title}`, "");
    parts.push(c.content_md || "", "");
  }
  return parts.join("\n").trimEnd() + "\n";
}

export function manualToMarkdown(guides, meta) {
  const head = ["# MunicipalConnect User Manual", "",
    `*Version v${meta?.number ?? ""} · ${meta?.label ?? ""} · ${meta?.release_tag ?? ""}*`, "", "## Contents", ""];
  for (const g of guides || []) {
    head.push(`- **${g.title}**`);
    for (const c of g.chapters || []) head.push(`  - ${c.title}`);
  }
  head.push("");
  return head.join("\n") + "\n---\n\n" + (guides || []).map(guideToMarkdown).join("\n\n---\n\n");
}

const saveTextFile = (text, filename) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/markdown;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Shared manual reader used by the Super Admin (User Manual) and tenant (Help) screens.
 * props:
 *  guides           – [{key,title,chapters:[{key,title,content_md,custom,edited}]}]
 *  versionMeta      – {number,label,release_tag,created_at}
 *  searchPath       – API path for search (q appended)
 *  exportPath       – API path for export (format/guide appended)
 *  toolbarExtra     – optional node rendered in the toolbar (e.g. version picker)
 *  onChapterAction  – optional (guide, chapter) => node rendered next to each chapter title (edit/delete)
 *  initialGuide     – guide key to open first
 *  offline          – true when the device has no network: search runs on-device, Markdown export is
 *                     built on-device and PDF export is disabled
 *  compact          – mobile layout: guide chips + collapsible contents instead of a side column
 */
export function ManualReader({ guides, versionMeta, searchPath, exportPath, toolbarExtra, onChapterAction, initialGuide, offline = false, compact = false }) {
  const [guideKey, setGuideKey] = useState(initialGuide || guides?.[0]?.key || COMPLETE_KEY);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [contentsOpen, setContentsOpen] = useState(false);
  const contentRef = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    if (guides?.length && guideKey !== COMPLETE_KEY && !guides.some((g) => g.key === guideKey)) {
      setGuideKey(guides[0].key);
    }
  }, [guides, guideKey]);

  const shownGuides = useMemo(
    () => (guideKey === COMPLETE_KEY ? guides || [] : (guides || []).filter((g) => g.key === guideKey)),
    [guides, guideKey],
  );
  const currentGuide = guideKey === COMPLETE_KEY ? null : shownGuides[0];

  // debounced search — server when online, on-device when offline (or when server fails)
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (q.trim().length < 2) { setResults(null); return; }
    debounce.current = setTimeout(() => {
      if (offline || !searchPath) { setResults(searchGuidesLocal(guides, q)); return; }
      setSearching(true);
      api.get(searchPath, { params: { q: q.trim() } })
        .then((r) => setResults(r.data.results || []))
        .catch(() => setResults(searchGuidesLocal(guides, q)))
        .finally(() => setSearching(false));
    }, 300);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [q, searchPath, offline, guides]);

  const jumpTo = (gKey, cKey) => {
    setGuideKey(gKey);
    setContentsOpen(false);
    setTimeout(() => {
      const el = document.getElementById(chapterDomId(gKey, cKey));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const doExport = async (format, guide, chapter) => {
    const gDoc = guide ? guides.find((g) => g.key === guide) : null;
    const cDoc = chapter && gDoc ? (gDoc.chapters || []).find((c) => c.key === chapter) : null;
    const gTitle = guide ? (gDoc?.title || guide) : "Complete";
    const fname = cDoc
      ? `MunicipalConnect-${cDoc.title.replace(/[^A-Za-z0-9]+/g, "-")}-v${versionMeta?.number ?? ""}.${format}`
      : `MunicipalConnect-Manual-v${versionMeta?.number ?? ""}-${gTitle.replace(/[^A-Za-z0-9]+/g, "-")}.${format}`;
    try {
      if (format === "md" && (offline || !exportPath)) {
        // built on the device — works without network
        const text = cDoc
          ? `# ${cDoc.title}\n\n_From the ${gTitle} — MunicipalConnect User Manual v${versionMeta?.number ?? ""}_\n\n${cDoc.content_md || ""}\n`
          : guide ? guideToMarkdown(gDoc) : manualToMarkdown(guides, versionMeta);
        saveTextFile(text, fname);
        toast.success("Markdown saved to this device");
        return;
      }
      if (format === "pdf" && offline) {
        toast.info("PDF export needs an internet connection — Markdown works offline");
        return;
      }
      const params = new URLSearchParams({ format });
      if (guide) params.set("guide", guide);
      if (chapter) params.set("chapter", chapter);
      await downloadBlob(`${exportPath}${exportPath.includes("?") ? "&" : "?"}${params.toString()}`, fname);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const total = (guides || []).reduce((s, g) => s + (g.chapters?.length || 0), 0);
  const pdfDisabled = offline;

  return (
    <div className="space-y-4" data-testid="manual-reader">
      {/* ---- toolbar ---- */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 max-w-xl">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${total} chapters…`}
            className="pl-9 pr-9" data-testid="manual-search-input" />
          {q && (
            <button className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setQ("")} aria-label="Clear search" data-testid="manual-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {toolbarExtra}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="manual-export-menu">
                <Download className="h-4 w-4 mr-1.5" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {pdfDisabled && (
                <p className="px-2 py-1.5 text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="manual-export-offline-note">
                  <WifiOff className="h-3 w-3" /> Offline — PDF needs internet, Markdown works
                </p>
              )}
              {currentGuide && (
                <>
                  <DropdownMenuLabel className="text-xs">{currentGuide.title}</DropdownMenuLabel>
                  <DropdownMenuItem disabled={pdfDisabled} onClick={() => doExport("pdf", currentGuide.key)} data-testid="manual-export-guide-pdf">
                    <FileText className="h-4 w-4 mr-2" /> Download as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doExport("md", currentGuide.key)} data-testid="manual-export-guide-md">
                    <FileText className="h-4 w-4 mr-2" /> Download as Markdown
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuLabel className="text-xs">Complete manual ({guides?.length || 0} guides)</DropdownMenuLabel>
              <DropdownMenuItem disabled={pdfDisabled} onClick={() => doExport("pdf")} data-testid="manual-export-all-pdf">
                <BookOpen className="h-4 w-4 mr-2" /> Download as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport("md")} data-testid="manual-export-all-md">
                <BookOpen className="h-4 w-4 mr-2" /> Download as Markdown
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ---- search results ---- */}
      {results !== null && (
        <Card className="shadow-sm" data-testid="manual-search-results">
          <CardContent className="p-0">
            <div className="px-4 py-2.5 border-b flex items-center justify-between">
              <p className="text-sm font-medium">
                {searching ? "Searching…" : `${results.length} chapter${results.length === 1 ? "" : "s"} match “${q}”`}
              </p>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setQ("")} data-testid="manual-search-close">Close</Button>
            </div>
            {results.length === 0 && !searching ? (
              <p className="text-sm text-muted-foreground p-4" data-testid="manual-search-empty">No chapters mention “{q}”. Try a different word.</p>
            ) : (
              <ul className="divide-y max-h-80 overflow-y-auto">
                {results.map((r) => (
                  <li key={`${r.guide_key}-${r.chapter_key}`}>
                    <button className="w-full text-left px-4 py-2.5 hover:bg-accent/50 transition-colors"
                      onClick={() => { jumpTo(r.guide_key, r.chapter_key); setQ(""); }}
                      data-testid={`manual-search-result-${r.guide_key}-${r.chapter_key}`}>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{r.guide_title}</span><ChevronRight className="h-3 w-3" />
                        <span className="font-medium text-foreground text-sm">{r.chapter_title}</span>
                        <Badge variant="outline" className="ml-auto text-[10px]">{r.matches} match{r.matches === 1 ? "" : "es"}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.snippet}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- body: nav + content ---- */}
      {compact && (
        <div className="space-y-2" data-testid="manual-compact-nav">
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" role="tablist">
            <button role="tab" aria-selected={guideKey === COMPLETE_KEY}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${guideKey === COMPLETE_KEY ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-accent"}`}
              onClick={() => setGuideKey(COMPLETE_KEY)} data-testid="manual-guide-complete">All guides</button>
            {(guides || []).map((g) => (
              <button key={g.key} role="tab" aria-selected={g.key === guideKey}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${g.key === guideKey ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-accent"}`}
                onClick={() => setGuideKey(g.key)} data-testid={`manual-guide-${g.key}`}>{g.title}</button>
            ))}
          </div>
          {currentGuide && (
            <Card className="shadow-sm">
              <button className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium"
                onClick={() => setContentsOpen((o) => !o)} aria-expanded={contentsOpen} data-testid="manual-contents-toggle">
                <span className="flex items-center gap-2"><ListTree className="h-4 w-4" /> Contents · {currentGuide.chapters?.length || 0} chapters</span>
                <ChevronRight className={`h-4 w-4 transition-transform ${contentsOpen ? "rotate-90" : ""}`} />
              </button>
              {contentsOpen && (
                <ul className="border-t px-2 py-1.5 space-y-0.5" data-testid={`manual-chapter-list-${currentGuide.key}`}>
                  {(currentGuide.chapters || []).map((c) => (
                    <li key={c.key}>
                      <button className="w-full text-left text-sm px-2 py-2 rounded-md hover:bg-accent flex items-center gap-2"
                        onClick={() => jumpTo(currentGuide.key, c.key)} data-testid={`manual-chapter-link-${currentGuide.key}-${c.key}`}>
                        <span className="truncate">{c.title}</span>
                        {c.custom && <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">Custom</Badge>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>
      )}
      <div className={compact ? "" : "grid lg:grid-cols-[280px_1fr] gap-4"}>
        {!compact && (
        <Card className="shadow-sm lg:sticky lg:top-4 self-start max-h-[calc(100vh-8rem)] overflow-y-auto">
          <CardContent className="p-2">
            <button
              className={`w-full text-left rounded-md px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${guideKey === COMPLETE_KEY ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
              onClick={() => setGuideKey(COMPLETE_KEY)} data-testid="manual-guide-complete">
              <BookOpen className="h-4 w-4" /> Complete manual
            </button>
            <div className="mt-1 space-y-0.5">
              {(guides || []).map((g) => {
                const active = g.key === guideKey;
                return (
                  <div key={g.key}>
                    <button
                      className={`w-full text-left rounded-md px-3 py-2 text-sm font-medium flex items-center justify-between transition-colors ${active ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
                      onClick={() => setGuideKey(g.key)} data-testid={`manual-guide-${g.key}`}>
                      <span className="truncate">{g.title}</span>
                      <span className={`text-[10px] tabular-nums ${active ? "opacity-80" : "text-muted-foreground"}`}>{g.chapters?.length || 0}</span>
                    </button>
                    {active && (
                      <ul className="ml-2 my-1 border-l pl-2 space-y-0.5" data-testid={`manual-chapter-list-${g.key}`}>
                        {(g.chapters || []).map((c) => (
                          <li key={c.key}>
                            <button className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground flex items-center gap-1.5"
                              onClick={() => jumpTo(g.key, c.key)} data-testid={`manual-chapter-link-${g.key}-${c.key}`}>
                              <span className="truncate">{c.title}</span>
                              {c.custom && <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">Custom</Badge>}
                              {c.edited && !c.custom && <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">Edited</Badge>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        )}

        <Card className="shadow-sm min-w-0">
          <CardContent className={compact ? "p-4" : "p-6 sm:p-8"} ref={contentRef} data-testid="manual-content">
            {versionMeta && (
              <p className="text-[11px] text-muted-foreground mb-4" data-testid="manual-version-note">
                Manual v{versionMeta.number} · {versionMeta.label}{versionMeta.release_tag ? ` · ${versionMeta.release_tag}` : ""}
              </p>
            )}
            {shownGuides.length === 0 ? (
              <p className="text-sm text-muted-foreground">No guides available.</p>
            ) : shownGuides.map((g, gi) => (
              <section key={g.key} className={gi > 0 ? "mt-12 pt-8 border-t-2" : ""} data-testid={`manual-guide-section-${g.key}`}>
                <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">{g.title}</h1>
                {(g.chapters || []).map((c) => (
                  <article key={c.key} id={chapterDomId(g.key, c.key)} className="scroll-mt-20 group" data-testid={`manual-chapter-${g.key}-${c.key}`}>
                    {!(c.key === "overview" && c.title === "Overview") && (
                      <div className="flex items-start justify-between gap-3 mt-8 mb-3 pb-1.5 border-b">
                        <h2 className="font-heading text-xl font-semibold tracking-tight flex items-center gap-2 flex-wrap">
                          {c.title}
                          {c.custom && <Badge variant="outline" className="text-[10px]">Custom</Badge>}
                          {c.edited && !c.custom && <Badge variant="outline" className="text-[10px]">Edited</Badge>}
                        </h2>
                        <div className="shrink-0 flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-foreground"
                            disabled={pdfDisabled} title={pdfDisabled ? "PDF needs an internet connection" : "Download this chapter as PDF"}
                            onClick={() => doExport("pdf", g.key, c.key)} aria-label={`Download ${c.title} as PDF`}
                            data-testid={`manual-chapter-pdf-${g.key}-${c.key}`}>
                            <Download className="h-3.5 w-3.5 mr-1" /> PDF
                          </Button>
                          {onChapterAction && onChapterAction(g, c)}
                        </div>
                      </div>
                    )}
                    {c.key === "overview" && c.title === "Overview" && onChapterAction && (
                      <div className="flex justify-end -mt-1 mb-2">{onChapterAction(g, c)}</div>
                    )}
                    <Markdown>{c.content_md}</Markdown>
                  </article>
                ))}
              </section>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
